<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <AppPageHeader design="new" title="Trengo" compact />
    <AppTabs design="new" refined weight="medium" :tabs="resourceTabs" :value="activeResource" @input="changeResource" />
    <main ref="page" class="ui-page" tabindex="-1">
      <AppFilterToolbar>
        <template #primary><app-input :value="search" design="new" type="search" :placeholder="definition.searchPlaceholder" aria-label="Search" @input.native="scheduleSearch" /></template>
        <app-select v-if="activeResource === 'channels'" :value="queryValue('type')" design="new" :options="channelTypeOptions" :reduce="option => option.value" label="label" placeholder="All channel types" aria-label="Channel type" @input="changeFilter('type', $event)" />
        <template v-if="activeResource === 'templates'">
          <app-select :value="queryValue('language')" design="new" :options="templateLanguageOptions" :reduce="option => option.value" label="label" placeholder="All languages" aria-label="Language" @input="changeFilter('language', $event)" />
          <app-select :value="queryValue('status')" design="new" :options="templateStatusOptions" :reduce="option => option.value" label="label" placeholder="All statuses" aria-label="Status" @input="changeFilter('status', $event)" />
        </template>
        <template v-if="['channels', 'users', 'contacts'].includes(activeResource)" #selection>
          <app-filters v-if="activeResource === 'channels'" :value="queryValue('privacy', 'all')" design="new" refined weight="medium" :filters="privacyFilters" name="trengoChannelPrivacy" aria-label="Privacy" @change="changeFilter('privacy', $event === 'all' ? null : $event)" />
          <app-filters v-else-if="activeResource === 'users'" :value="queryValue('mapping', 'all')" design="new" refined weight="medium" :filters="mappingFilters" name="trengoUserMapping" aria-label="Mapping" @change="changeFilter('mapping', $event === 'all' ? null : $event)" />
          <app-filters v-else :value="queryValue('contact_state', 'all')" design="new" refined weight="medium" :filters="contactStateFilters" name="trengoContactState" aria-label="Contact state" @change="changeFilter('contact_state', $event === 'all' ? null : $event)" />
        </template>
        <template #actions><AppIconButton label="Refresh" tooltip-placement="left" :disabled="loading" @click="load"><i class="pi pi-refresh" aria-hidden="true" /></AppIconButton></template>
        <template v-if="hasFilters || search" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
      </AppFilterToolbar>
      <AppErrorState v-if="optionsError" :message="optionsError" icon-only retry-label="Retry filter options" @retry="loadOptions" />
      <AppContentSkeleton v-if="loading" :label="`Loading ${definition.label}`" :columns="definition.columns" />
      <AppErrorState v-else-if="error" :message="error" icon-only @retry="load" />
      <AppCard v-else-if="rows.length" design="new" variant="table" header-density="compact">
        <AppDataTable design="new" density="dense" :rows="rows" :columns="definition.columns" clickable @row-click="openDetail">
          <template #cell="{ row, column }">
            <AppTableUser v-if="column.kind === 'mapped-user'" :name="row.mapped_user ? cellValue(row, column) : ''" :photo="row.mapped_user && row.mapped_user.photo || ''" />
            <AppTableUser v-else-if="activeResource === 'users' && column.key === 'name'" :name="cellValue(row, column) === '—' ? '' : cellValue(row, column)" />
            <AppStatusBadge v-else-if="column.kind === 'privacy'" design="new" compact :tone="row[column.key] === true ? 'blue' : 'neutral'" :label="privacyLabel(row[column.key])" />
            <AppStatusBadge v-else-if="column.kind === 'active'" design="new" compact :tone="row[column.key] ? 'green' : 'neutral'" :label="row[column.key] ? 'Active' : 'Inactive'" />
            <AppStatusBadge v-else-if="column.kind === 'status' && row[column.key]" design="new" compact tone="neutral" :label="String(row[column.key])" />
            <span v-else-if="column.kind === 'color'" class="ui-cell-inline"><span v-if="row[column.key]" class="trengo-color" :style="{ backgroundColor: row[column.key] }" aria-hidden="true" /><AppTableText :text="cellValue(row, column)" /></span>
            <AppTableText v-else :text="cellValue(row, column)" />
          </template>
        </AppDataTable>
      </AppCard>
      <app-no-results v-else design="new" :title="`No ${definition.label.toLowerCase()} found`" text="" />
      <AppPagination v-if="!loading && !error" design="new" placement="embedded" :page="page" :pages="pages" :count="count" :visible-count="rows.length" :page-size="PAGE_SIZE" :loading="loading" :label="`${definition.label} pages`" @change="changePage" />
    </main>
    <ResourceModal v-if="selectedModalId" :key="`${activeResource}:${selectedModalId}`" :resource="activeResource" :resource-id="selectedModalId" @close="closeRouteModal" @open-related="openRelated" />
  </AppPageContainer>
</template>
<script>
import { format } from 'date-fns'
import { getTrengoResourceOptions, getTrengoResources } from '@/http'
import AppTabs from '@/components/AppTabs'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import ResourceModal from './ResourceModal.vue'
import { modalRoute } from '@/views/crm/modalRoute'

const PAGE_SIZE = 20
const RESOURCES = {
  channels: {
    label: 'Channels',
    searchPlaceholder: 'Channel name, Trengo ID or type',
    columns: [
      { key: 'display_name', label: 'Channel', fallback: 'name', width: '44%' },
      { key: 'external_id', label: 'Trengo ID', width: '14%' },
      { key: 'channel_type', label: 'Type', width: '14%' },
      { key: 'is_private', label: 'Privacy', kind: 'privacy', width: '14%' },
      { key: 'active', label: 'State', kind: 'active', width: '14%' }
    ]
  },
  users: {
    label: 'Users',
    searchPlaceholder: 'Trengo user, Trengo ID or mapped Board user',
    columns: [
      { key: 'name', label: 'Trengo user', width: '34%' },
      { key: 'external_id', label: 'Trengo ID', width: '16%' },
      { key: 'mapped_user', label: 'Mapped Board user', kind: 'mapped-user', width: '36%' },
      { key: 'active', label: 'State', kind: 'active', width: '14%' }
    ]
  },
  labels: {
    label: 'Labels',
    searchPlaceholder: 'Label name, color or Trengo ID',
    columns: [
      { key: 'name', label: 'Label', width: '35%' },
      { key: 'external_id', label: 'Trengo ID', width: '18%' },
      { key: 'color', label: 'Color', kind: 'color', width: '16%' },
      { key: 'last_seen_at', label: 'Last seen', kind: 'date', width: '18%' },
      { key: 'active', label: 'State', kind: 'active', width: '13%' }
    ]
  },
  teams: {
    label: 'Teams',
    searchPlaceholder: 'Team name or Trengo ID',
    columns: [
      { key: 'name', label: 'Team', width: '45%' },
      { key: 'external_id', label: 'Trengo ID', width: '20%' },
      { key: 'last_seen_at', label: 'Last seen', kind: 'date', width: '22%' },
      { key: 'active', label: 'State', kind: 'active', width: '13%' }
    ]
  },
  templates: {
    label: 'WhatsApp Templates',
    searchPlaceholder: 'Template title, slug, message or Trengo ID',
    columns: [
      { key: 'title', label: 'Template', fallback: 'slug', width: '35%' },
      { key: 'language', label: 'Language', width: '15%' },
      { key: 'status', label: 'Status', kind: 'status', width: '15%' },
      { key: 'channels', label: 'Channels', kind: 'count', width: '20%' },
      { key: 'active', label: 'State', kind: 'active', width: '15%' }
    ]
  },
  contacts: {
    label: 'Contacts',
    searchPlaceholder: 'Contact name, email, phone or Trengo ID',
    columns: [
      { key: 'name', label: 'Contact', width: '20%' },
      { key: 'email', label: 'Email', width: '20%' },
      { key: 'phone', label: 'Phone', width: '14%' },
      { key: 'external_id', label: 'Trengo ID', width: '14%' },
      { key: 'last_seen_at', label: 'Last seen', kind: 'date', width: '20%' },
      { key: 'active', label: 'State', kind: 'active', width: '12%' }
    ]
  }
}

export default {
  name: 'TechSupportTrengo',
  components: { AppTabs, AppContentSkeleton, AppErrorState, AppIconButton, AppTableText, AppTableUser, ResourceModal },
  mixins: [modalRoute('item')],
  data() {
    return {
      PAGE_SIZE,
      rows: [],
      count: 0,
      pages: 0,
      loading: false,
      error: '',
      optionsError: '',
      search: '',
      options: {
        channel_types: [],
        template_languages: [],
        template_statuses: []
      },
      searchTimer: null,
      requestId: 0
    }
  },
  computed: {
    activeResource() {
      return Object.prototype.hasOwnProperty.call(RESOURCES, this.$route.params.resource) ? this.$route.params.resource : 'channels'
    },
    definition() {
      return RESOURCES[this.activeResource]
    },
    resourceTabs() {
      return Object.keys(RESOURCES).map(value => ({
        id: value,
        value,
        label: RESOURCES[value].label
      }))
    },
    privacyFilters() {
      return [
        { value: 'all', label: 'All' },
        { value: 'private', label: 'Private' },
        { value: 'shared', label: 'Shared' }
      ]
    },
    mappingFilters() {
      return [
        { value: 'all', label: 'All' },
        { value: 'mapped', label: 'Mapped' },
        { value: 'unmapped', label: 'Unmapped' }
      ]
    },
    contactStateFilters() {
      return [
        { value: 'all', label: 'All' },
        { value: 'current', label: 'Current' },
        { value: 'deleted', label: 'Deleted' }
      ]
    },
    channelTypeOptions() {
      return this.optionList(this.options.channel_types)
    },
    templateLanguageOptions() {
      return this.optionList(this.options.template_languages)
    },
    templateStatusOptions() {
      return this.optionList(this.options.template_statuses)
    },
    page() {
      const page = Number(this.$route.query.page || 1)
      return Number.isSafeInteger(page) && page > 0 ? page : 1
    },
    hasFilters() {
      return Object.keys(this.$route.query).some(key => !['page', 'item'].includes(key))
    },
    listKey() {
      return JSON.stringify([this.activeResource, this.$route.query])
    }
  },
  watch: {
    '$route.params.resource': {
      immediate: true,
      handler(resource) {
        if (!Object.prototype.hasOwnProperty.call(RESOURCES, resource)) {
          this.$router.replace({
            name: 'tech-support-trengo',
            params: { resource: 'channels' },
            query: this.$route.query
          }).catch(() => {})
        }
      }
    },
    '$route.query.search': {
      immediate: true,
      handler(value) {
        window.clearTimeout(this.searchTimer)
        this.search = typeof value === 'string' ? value : ''
      }
    },
    'listKey': {
      immediate: true,
      handler() {
        this.load()
      }
    }
  },
  created() {
    this.loadOptions()
  },
  beforeDestroy() {
    window.clearTimeout(this.searchTimer)
    this.requestId += 1
  },
  methods: {
    async loadOptions() {
      this.optionsError = ''
      try {
        const { data } = await getTrengoResourceOptions()
        this.options = data
      } catch (error) {
        this.optionsError = 'Unable to load Trengo filter options'
        this.$notify({ type: 'error', title: 'Unable to load Trengo filter options' })
      }
    },
    async load() {
      if (!Object.prototype.hasOwnProperty.call(RESOURCES, this.$route.params.resource)) return
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const { data } = await getTrengoResources(this.activeResource, this.requestParams())
        if (requestId !== this.requestId) return
        this.rows = data.results
        this.count = data.count
        this.pages = data.pages
      } catch (error) {
        if (requestId === this.requestId) this.error = this.errorMessage(error)
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    requestParams() {
      const params = {
        page: this.page,
        page_size: PAGE_SIZE
      }
      const search = this.queryValue('search').trim()
      if (search) params.search = search
      if (this.activeResource === 'channels') {
        if (this.queryValue('privacy')) params.privacy = this.queryValue('privacy')
        if (this.queryValue('type')) params.channel_type = this.queryValue('type')
      }
      if (this.activeResource === 'users' && this.queryValue('mapping')) {
        params.mapping = this.queryValue('mapping')
      }
      if (this.activeResource === 'templates') {
        if (this.queryValue('language')) params.language = this.queryValue('language')
        if (this.queryValue('status')) params.status = this.queryValue('status')
      }
      if (this.activeResource === 'contacts' && this.queryValue('contact_state')) {
        params.is_deleted = this.queryValue('contact_state') === 'deleted'
      }
      return params
    },
    queryValue(key, fallback = '') {
      const value = this.$route.query[key]
      return typeof value === 'string' ? value : fallback
    },
    optionList(values) {
      return (values || []).map(value => ({ value, label: String(value).replace(/_/g, ' ') }))
    },
    changeResource(resource) {
      if (resource === this.activeResource) return
      this.$router.push({
        name: 'tech-support-trengo',
        params: { resource },
        query: {}
      }).catch(() => {})
    },
    scheduleSearch(event) {
      this.search = event && event.target ? event.target.value : ''
      window.clearTimeout(this.searchTimer)
      this.searchTimer = window.setTimeout(() => this.changeFilter('search', this.search), 350)
    },
    changeFilter(key, value) {
      const query = { ...this.$route.query }
      delete query.item
      delete query.page
      if (value === null || value === undefined || value === '') delete query[key]
      else query[key] = String(value)
      this.$router.push({ query }).catch(() => {})
    },
    clearFilters() {
      window.clearTimeout(this.searchTimer)
      this.search = ''
      const query = {}
      if (this.selectedModalId) query.item = String(this.selectedModalId)
      this.$router.push({ query }).catch(() => {})
    },
    changePage(page) {
      const query = { ...this.$route.query, page: String(page) }
      delete query.item
      this.$router.push({ query }).catch(() => {})
      this.$nextTick(() => this.$refs.page && this.$refs.page.focus())
    },
    openDetail({ row, event }) {
      this.openRouteModal(row.id, event && event.currentTarget)
    },
    openRelated({ resource, id }) {
      this.$router.push({
        name: 'tech-support-trengo',
        params: { resource },
        query: { item: String(id) }
      }).catch(() => {})
    },
    privacyLabel(value) {
      return value === true ? 'Private' : value === false ? 'Shared' : 'Unknown'
    },
    cellValue(row, column) {
      if (column.kind === 'mapped-user') {
        const user = row[column.key]
        return user ? user.full_name || user.email || `Board user #${user.id}` : 'Not mapped'
      }
      if (column.kind === 'count') return (row[column.key] || []).length
      if (column.kind === 'date') {
        return row[column.key] ? format(new Date(row[column.key]), 'dd MMM yyyy, HH:mm') : '—'
      }
      let value = row[column.key]
      if ((value === null || value === undefined || value === '') && column.fallback) {
        value = row[column.fallback]
      }
      return value === null || value === undefined || value === '' ? '—' : value
    },
    errorMessage(error) {
      const data = error.response && error.response.data
      return (data && (data.detail || data.message)) || `Unable to load ${this.definition.label.toLowerCase()}.`
    }
  }
}
</script>
<style lang="scss" scoped>
.trengo-color { width: 12px; height: 12px; flex: 0 0 12px; border: 1px solid $ui-line-strong; border-radius: 50%; }
</style>
